export type ThemeMode = 'adaptive' | 'dark';

type ThemeRule = readonly [pattern: string, mode: ThemeMode];

const LOCALES: readonly string[] = ['en', 'fr'];

/**
 * Single source of truth for which pages follow the user's light/dark choice
 * ('adaptive', header toggle shown) and which are always dark ('dark').
 *
 * Patterns are matched against the path without its locale prefix. ':name'
 * matches exactly one segment. The first matching rule wins and any path not
 * listed is 'dark'. To change a page, add or edit one line here.
 *
 * The dashboard, login and register live in the (unlocalized) root layout,
 * which has its own ThemeProvider and never renders MainLayout, so they are not
 * governed by this list.
 */
const THEME_ROUTES: readonly ThemeRule[] = [
  // Always dark. These come first so they win over the broader adaptive patterns below.
  ['/', 'dark'],
  ['/tools', 'dark'],
  ['/about', 'dark'],
  ['/tools/tags/:tag', 'dark'],
  ['/share/favorites/:userId', 'dark'],

  // Adaptive
  ['/insights', 'adaptive'],
  ['/insights/:slug', 'adaptive'],
  ['/help', 'adaptive'],
  ['/help/:slug', 'adaptive'],
  ['/compare', 'adaptive'],
  ['/compare/:slugs', 'adaptive'],
  ['/:profession/:subcategory', 'adaptive'],
  ['/:profession/:subcategory/:tool', 'adaptive'],
  ['/:profession/:subcategory/:tool/:nested', 'adaptive'],

  // ai-for-{profession} landing pages
  ['/:profession', 'dark'],
];

const toSegments = (path: string) => path.split('/').filter(Boolean);

const COMPILED_RULES = THEME_ROUTES.map(([pattern, mode]) => [toSegments(pattern), mode] as const);

export function getThemeMode(pathname: string): ThemeMode {
  const segments = toSegments(pathname);
  if (LOCALES.includes(segments[0])) segments.shift();

  const rule = COMPILED_RULES.find(
    ([pattern]) =>
      pattern.length === segments.length &&
      pattern.every((part, index) => part.startsWith(':') || part === segments[index]),
  );

  return rule ? rule[1] : 'dark';
}
